import React from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// CSS to style the datepicker similar to existing inputs
import './LocalizedDatePicker.css';

interface LocalizedDatePickerProps {
    value: string; // ISO YYYY-MM-DD
    onChange: (value: string) => void;
    format?: string; // e.g. DD/MM/YYYY
    placeholder?: string;
    className?: string;
}

export const LocalizedDatePicker: React.FC<LocalizedDatePickerProps> = ({
    value,
    onChange,
    format = 'DD/MM/YYYY',
    placeholder,
    className
}) => {
    // Map common user formats to date-fns/react-datepicker format
    const getDateFormat = (fmt: string) => {
        return fmt
            .replace('DD', 'dd')
            .replace('YYYY', 'yyyy');
    };

    const selectedDate = React.useMemo(() => {
        if (!value) return null;
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }, [value]);

    const handleChange = (date: Date | null) => {
        if (!date) {
            onChange('');
            return;
        }

        // Format to YYYY-MM-DD manually to avoid timezone issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const isoString = `${year}-${month}-${day}`;

        onChange(isoString);
    };

    return (
        <DatePicker
            selected={selectedDate}
            onChange={handleChange}
            dateFormat={getDateFormat(format)}
            placeholderText={placeholder || format}
            className={className}
            // Portal prevents overflow clipping in tight spaces (like tables or sidebars)
            popperPlacement="bottom-start"
            portalId="root-portal"
            enableTabLoop={false}
            strictParsing
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
        />
    );
};
